// IndexedDB wrapper for offline data storage
// Provides persistent storage larger than localStorage (typically 50MB+)

const DB_NAME = 'athenea_offline_db';
const DB_VERSION = 1;

// Store names
export const STORES = {
  WORK_ORDERS: 'work_orders',
  PROCESS_RECORDS: 'process_records',
  PENDING_ACTIONS: 'pending_actions',
  CACHE_DATA: 'cache_data',
  NFC_CAPTURES: 'nfc_captures',
} as const;

type StoreName = typeof STORES[keyof typeof STORES];

let db: IDBDatabase | null = null;

// Initialize the database
export async function initDB(): Promise<IDBDatabase> {
  if (db) return db;

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      console.error('[OfflineDB] Error opening database:', request.error);
      reject(request.error);
    };

    request.onsuccess = () => {
      db = request.result;
      console.log('[OfflineDB] Database opened successfully');
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const database = (event.target as IDBOpenDBRequest).result;
      console.log('[OfflineDB] Upgrading database...');

      // Work Orders store
      if (!database.objectStoreNames.contains(STORES.WORK_ORDERS)) {
        const workOrdersStore = database.createObjectStore(STORES.WORK_ORDERS, { keyPath: 'id' });
        workOrdersStore.createIndex('status', 'status', { unique: false });
        workOrdersStore.createIndex('updated_at', 'updated_at', { unique: false });
      }

      // Process Records store
      if (!database.objectStoreNames.contains(STORES.PROCESS_RECORDS)) {
        const processStore = database.createObjectStore(STORES.PROCESS_RECORDS, { keyPath: 'id' });
        processStore.createIndex('work_order_id', 'work_order_id', { unique: false });
        processStore.createIndex('status', 'status', { unique: false });
      }

      // Pending Actions store (for sync queue)
      if (!database.objectStoreNames.contains(STORES.PENDING_ACTIONS)) {
        const pendingStore = database.createObjectStore(STORES.PENDING_ACTIONS, { keyPath: 'id' });
        pendingStore.createIndex('timestamp', 'timestamp', { unique: false });
        pendingStore.createIndex('type', 'type', { unique: false });
        pendingStore.createIndex('synced', 'synced', { unique: false });
      }

      // Cache Data store (for general API responses)
      if (!database.objectStoreNames.contains(STORES.CACHE_DATA)) {
        const cacheStore = database.createObjectStore(STORES.CACHE_DATA, { keyPath: 'key' });
        cacheStore.createIndex('expires_at', 'expires_at', { unique: false });
      }

      // NFC Captures store
      if (!database.objectStoreNames.contains(STORES.NFC_CAPTURES)) {
        const nfcStore = database.createObjectStore(STORES.NFC_CAPTURES, { keyPath: 'id' });
        nfcStore.createIndex('captured_at', 'captured_at', { unique: false });
        nfcStore.createIndex('synced', 'synced', { unique: false });
      }
    };
  });
}

// Generic get all items from store
export async function getAll<T>(storeName: StoreName): Promise<T[]> {
  const database = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    const request = store.getAll();

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

// Generic get item by id
export async function getById<T>(storeName: StoreName, id: string): Promise<T | undefined> {
  const database = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    const request = store.get(id);

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

// Generic put (add or update) item
export async function put<T extends { id: string }>(storeName: StoreName, item: T): Promise<void> {
  const database = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.put(item);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

// Generic put multiple items
export async function putMany<T extends { id: string }>(storeName: StoreName, items: T[]): Promise<void> {
  const database = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);

    let completed = 0;
    items.forEach(item => {
      const request = store.put(item);
      request.onsuccess = () => {
        completed++;
        if (completed === items.length) resolve();
      };
      request.onerror = () => reject(request.error);
    });

    if (items.length === 0) resolve();
  });
}

// Generic delete item
export async function remove(storeName: StoreName, id: string): Promise<void> {
  const database = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.delete(id);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

// Clear entire store
export async function clearStore(storeName: StoreName): Promise<void> {
  const database = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.clear();

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

// Get items by index
export async function getByIndex<T>(
  storeName: StoreName,
  indexName: string,
  value: IDBValidKey | boolean
): Promise<T[]> {
  const database = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    const index = store.index(indexName);

    // Use cursor for boolean values since getAll doesn't support them directly
    if (typeof value === 'boolean') {
      const results: T[] = [];
      const request = index.openCursor();

      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
        if (cursor) {
          if (cursor.value[indexName] === value) {
            results.push(cursor.value);
          }
          cursor.continue();
        } else {
          resolve(results);
        }
      };
      request.onerror = () => reject(request.error);
    } else {
      const request = index.getAll(value);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    }
  });
}

// Count items in store
export async function count(storeName: StoreName): Promise<number> {
  const database = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    const request = store.count();

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

// Pending Actions specific functions
export interface PendingAction {
  id: string;
  type: 'create' | 'update' | 'delete';
  entity: 'work_order' | 'process_record' | 'nfc_capture' | 'quality_control';
  data: any;
  endpoint: string;
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  timestamp: number;
  synced: boolean;
  retries: number;
  lastError?: string;
}

export async function addPendingAction(
  action: Omit<PendingAction, 'id' | 'timestamp' | 'synced' | 'retries'>
): Promise<PendingAction> {
  const pendingAction: PendingAction = {
    ...action,
    id: crypto.randomUUID(),
    timestamp: Date.now(),
    synced: false,
    retries: 0,
  };
  await put(STORES.PENDING_ACTIONS, pendingAction);
  return pendingAction;
}

export async function getPendingActions(): Promise<PendingAction[]> {
  const actions = await getByIndex<PendingAction>(STORES.PENDING_ACTIONS, 'synced', false);
  return actions.sort((a, b) => a.timestamp - b.timestamp);
}

export async function markActionSynced(id: string): Promise<void> {
  const action = await getById<PendingAction>(STORES.PENDING_ACTIONS, id);
  if (action) {
    action.synced = true;
    await put(STORES.PENDING_ACTIONS, action);
  }
}

export async function markActionFailed(id: string, error: string): Promise<void> {
  const action = await getById<PendingAction>(STORES.PENDING_ACTIONS, id);
  if (action) {
    action.retries++;
    action.lastError = error;
    await put(STORES.PENDING_ACTIONS, action);
  }
}

export async function removeSyncedActions(): Promise<void> {
  const allActions = await getAll<PendingAction>(STORES.PENDING_ACTIONS);
  const syncedActions = allActions.filter(a => a.synced);
  for (const action of syncedActions) {
    await remove(STORES.PENDING_ACTIONS, action.id);
  }
}

// Cache functions
interface CacheEntry {
  key: string;
  data: any;
  expires_at: number;
}

export async function setCache(key: string, data: any, ttlSeconds: number = 300): Promise<void> {
  const entry: CacheEntry = {
    key,
    data,
    expires_at: Date.now() + (ttlSeconds * 1000),
  };
  await put(STORES.CACHE_DATA, entry as any);
}

export async function getCache<T>(key: string): Promise<T | null> {
  const entry = await getById<CacheEntry>(STORES.CACHE_DATA, key);
  if (!entry) return null;
  if (entry.expires_at < Date.now()) {
    await remove(STORES.CACHE_DATA, key);
    return null;
  }
  return entry.data as T;
}

export async function clearExpiredCache(): Promise<void> {
  const allCache = await getAll<CacheEntry>(STORES.CACHE_DATA);
  const now = Date.now();
  for (const entry of allCache) {
    if (entry.expires_at < now) {
      await remove(STORES.CACHE_DATA, entry.key);
    }
  }
}

// Initialize DB on import
initDB().catch(console.error);
